import { useState } from "react";
import { usePage } from "@inertiajs/react";
import { TableCellsIcon, SpeakerWaveIcon, PlusIcon, WindowIcon } from "@heroicons/react/24/outline";
import { getTextareaRef } from "@lib/brainstormTextarea";
import CreateCampaignModal from "@components/ads/CreateCampaignModal";
import { DotPaginator } from "@components/ui/DotPaginator";
import { cn } from "@lib/utils";
import { SiteRow, AdRow, type AdMini, type SiteMini } from "./JumpBackRows";

const PAGE_SIZE = 4;
type Tab = "all" | "sites" | "ads";
const TABS: { key: Tab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "sites", label: "Sites" },
  { key: "ads", label: "Ads" },
];

/**
 * "Jump back in" hub on the brainstorm landing page. Two independently
 * paginated columns (Sites, Ads) with an All/Sites/Ads switch between the
 * 2-up split and a single full-width column. Renders nothing when the account
 * has neither sites nor ads. Data arrives batched from ProjectsController#new
 * and paginates client-side.
 */
export function JumpBackIn() {
  const props = usePage().props as { recent_projects?: SiteMini[]; recent_ads?: AdMini[] };
  const sites = props.recent_projects ?? [];
  const ads = props.recent_ads ?? [];

  const [tab, setTab] = useState<Tab>("all");
  const [sitesPage, setSitesPage] = useState(0);
  const [adsPage, setAdsPage] = useState(0);
  const [adModalOpen, setAdModalOpen] = useState(false);

  if (sites.length === 0 && ads.length === 0) return null;

  const focusChat = () => {
    const textarea = getTextareaRef().current;
    textarea?.scrollIntoView({ behavior: "smooth", block: "center" });
    textarea?.focus();
  };

  const showSites = tab === "all" || tab === "sites";
  const showAds = tab === "all" || tab === "ads";

  return (
    <div className="w-full max-w-5xl mx-auto mt-16 pb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-mono text-xs font-medium uppercase tracking-widest text-neutral-400">
          Jump back in
        </h2>
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-neutral-100 p-0.5">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                "rounded-md px-3 py-1 text-sm transition-colors",
                tab === key
                  ? "bg-white font-medium text-base-600 shadow-sm"
                  : "text-base-400 hover:text-base-500"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={cn("grid gap-6", tab === "all" ? "md:grid-cols-2" : "grid-cols-1")}>
        {showSites && (
          <Column
            icon={<WindowIcon className="w-5 h-5 text-orange-500" />}
            iconBg="bg-orange-100"
            label="Sites"
            count={sites.length}
            onNew={focusChat}
            page={sitesPage}
            onPage={setSitesPage}
            itemCount={sites.length}
            empty={<SitesEmpty onNew={focusChat} />}
          >
            {sites.slice(sitesPage * PAGE_SIZE, sitesPage * PAGE_SIZE + PAGE_SIZE).map((site) => (
              <SiteRow key={site.id} project={site} />
            ))}
          </Column>
        )}
        {showAds && (
          <Column
            icon={<SpeakerWaveIcon className="w-5 h-5 text-primary-600" />}
            iconBg="bg-primary-100"
            label="Ads"
            count={ads.length}
            onNew={() => setAdModalOpen(true)}
            page={adsPage}
            onPage={setAdsPage}
            itemCount={ads.length}
            empty={<AdsEmpty onNew={() => setAdModalOpen(true)} />}
          >
            {ads.slice(adsPage * PAGE_SIZE, adsPage * PAGE_SIZE + PAGE_SIZE).map((ad) => (
              <AdRow key={ad.id} ad={ad} />
            ))}
          </Column>
        )}
      </div>

      <CreateCampaignModal open={adModalOpen} onOpenChange={setAdModalOpen} />
    </div>
  );
}

interface ColumnProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  count: number;
  onNew: () => void;
  page: number;
  onPage: (page: number) => void;
  itemCount: number;
  empty: React.ReactNode;
  children: React.ReactNode;
}

function Column({ icon, iconBg, label, count, onNew, page, onPage, itemCount, empty, children }: ColumnProps) {
  const pageCount = Math.max(1, Math.ceil(itemCount / PAGE_SIZE));

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className={cn("w-9 h-9 shrink-0 rounded-xl flex items-center justify-center", iconBg)}>
          {icon}
        </div>
        <h3 className="font-sans text-base font-semibold text-base-600">{label}</h3>
        <span className="font-mono text-sm text-neutral-400">{count}</span>
        <div className="flex-1" />
        <NewButton onClick={onNew} />
      </div>

      {itemCount === 0 ? empty : <div className="flex flex-col gap-4">{children}</div>}

      <DotPaginator
        page={page}
        pageCount={pageCount}
        onChange={onPage}
        className="justify-center mt-4"
        label={`${label} pages`}
      />
    </section>
  );
}

function NewButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-2.5 py-1 text-sm text-base-500 transition-colors hover:border-neutral-400"
    >
      <PlusIcon className="w-4 h-4" />
      New
    </button>
  );
}

function AdsEmpty({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-neutral-300 bg-white/50 px-6 py-10 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50">
        <SpeakerWaveIcon className="h-7 w-7 text-primary-600" />
      </div>
      <h4 className="font-sans text-lg font-semibold text-base-600">No ads yet</h4>
      <p className="mt-2 max-w-xs font-sans text-sm text-neutral-500">
        Turn a site into Google Ads in a click — we'll help you write the copy and wire up tracking.
      </p>
      <button
        type="button"
        onClick={onNew}
        className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
      >
        <PlusIcon className="h-4 w-4" />
        Create your first ad
      </button>
      <p className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs text-neutral-400">
        <TableCellsIcon className="h-3.5 w-3.5" />
        pulls headlines from your live site
      </p>
    </div>
  );
}

function SitesEmpty({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-neutral-300 bg-white/50 px-6 py-10 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-50">
        <TableCellsIcon className="h-7 w-7 text-orange-500" />
      </div>
      <h4 className="font-sans text-lg font-semibold text-base-600">No sites yet</h4>
      <p className="mt-2 max-w-xs font-sans text-sm text-neutral-500">
        Describe your business above and we'll build your first landing page.
      </p>
      <button
        type="button"
        onClick={onNew}
        className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
      >
        <PlusIcon className="h-4 w-4" />
        Start a site
      </button>
    </div>
  );
}
